import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Monitor, Loader2, Unplug } from 'lucide-react';
import {
  connectQuickstartDisplay,
  disconnectQuickstartDisplay,
  fetchQuickstartDisplayStatus,
  fetchQuickstartDisplayConnection,
} from '../lib/unterrichtenApi';
import { teacherDisableLernbueroDashboard } from '../lib/displayApi';
import DisplayMirrorPanel from '../components/DisplayMirrorPanel';

export default function DisplayMirrorPage() {
  const [displayPin, setDisplayPin] = useState('');
  const [connectedDisplayPin, setConnectedDisplayPin] = useState<string | null>(null);
  const [showDisplayConnectForm, setShowDisplayConnectForm] = useState(true);
  const [displayConnectLoading, setDisplayConnectLoading] = useState(false);
  const [displayDisconnectLoading, setDisplayDisconnectLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadActiveDisplayConnection = async () => {
      try {
        const connection = await fetchQuickstartDisplayConnection();
        if (cancelled) return;
        if (connection?.pin && connection.status === 'connected') {
          setConnectedDisplayPin(connection.pin);
          setDisplayPin(connection.pin);
          setShowDisplayConnectForm(false);
          try {
            await teacherDisableLernbueroDashboard(connection.pin);
          } catch {
            // best-effort: Mirror soll auch ohne harte Abbruchlogik nutzbar bleiben
          }
        } else {
          setConnectedDisplayPin(null);
          setShowDisplayConnectForm(true);
        }
      } catch {
        if (!cancelled) {
          setConnectedDisplayPin(null);
          setShowDisplayConnectForm(true);
        }
      }
    };

    loadActiveDisplayConnection();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!connectedDisplayPin) return;

    const interval = window.setInterval(async () => {
      try {
        const status = await fetchQuickstartDisplayStatus(connectedDisplayPin);
        if (status.status !== 'connected') {
          setConnectedDisplayPin(null);
          setShowDisplayConnectForm(true);
          setDisplayPin('');
          toast('Display-Verbindung wurde beendet.');
        }
      } catch (error: unknown) {
        const err = error as { response?: { status?: number } };
        if (err?.response?.status === 404) {
          setConnectedDisplayPin(null);
          setShowDisplayConnectForm(true);
          setDisplayPin('');
        }
      }
    }, 15000);

    return () => window.clearInterval(interval);
  }, [connectedDisplayPin]);

  const handleConnectDisplay = async () => {
    const pin = displayPin.trim().toUpperCase();
    if (!/^[A-Z0-9]{6}$/.test(pin)) {
      toast.error('Bitte eine gültige 6-stellige PIN (A-Z, 0-9) eingeben');
      return;
    }

    setDisplayConnectLoading(true);
    try {
      if (connectedDisplayPin && connectedDisplayPin !== pin) {
        await disconnectQuickstartDisplay(connectedDisplayPin);
      }
      await connectQuickstartDisplay({ pin });
      try {
        await teacherDisableLernbueroDashboard(pin);
      } catch {
        // best-effort: bei Fehler trotzdem gekoppelt lassen
      }
      setConnectedDisplayPin(pin);
      setShowDisplayConnectForm(false);
      toast.success(`Display ${pin} verbunden`);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; message?: string } } };
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Display konnte nicht verbunden werden';
      toast.error(message);
    } finally {
      setDisplayConnectLoading(false);
    }
  };

  const handleDisconnectDisplay = async () => {
    if (!connectedDisplayPin) return;
    const confirmed = window.confirm(`Display ${connectedDisplayPin} wirklich entkoppeln?`);
    if (!confirmed) return;

    setDisplayDisconnectLoading(true);
    try {
      await disconnectQuickstartDisplay(connectedDisplayPin);
      setConnectedDisplayPin(null);
      setShowDisplayConnectForm(true);
      setDisplayPin('');
      toast.success('Display-Kopplung entfernt');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; message?: string } } };
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Display-Kopplung konnte nicht entfernt werden';
      toast.error(message);
    } finally {
      setDisplayDisconnectLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-6rem)] flex-col gap-4 p-4">
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 pb-4 dark:border-gray-700">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">Display-Steuerung</h1>
        <div className="flex items-center gap-2">
          {(showDisplayConnectForm || !connectedDisplayPin) && (
            <div className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 dark:border-sky-800 dark:bg-sky-950/40">
              <input
                value={displayPin}
                onChange={(e) =>
                  setDisplayPin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))
                }
                className="input-field w-28 font-mono tracking-[0.2em]"
                placeholder="PIN"
              />
              <button
                type="button"
                onClick={handleConnectDisplay}
                disabled={displayConnectLoading}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-sky-300 bg-white text-sky-700 hover:bg-sky-100 disabled:opacity-50 dark:border-sky-600 dark:bg-sky-900/50 dark:text-sky-200 dark:hover:bg-sky-900"
                title="Display koppeln"
              >
                {displayConnectLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Monitor className="h-4 w-4" />
                )}
              </button>
            </div>
          )}
          {connectedDisplayPin && (
            <>
              <span className="font-mono text-sm text-slate-600 dark:text-slate-400">{connectedDisplayPin}</span>
              <button
                type="button"
                onClick={handleDisconnectDisplay}
                disabled={displayDisconnectLoading}
                className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-950/60"
                title="Display entkoppeln"
              >
                {displayDisconnectLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Unplug className="h-4 w-4" />
                )}
                Entkoppeln
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card min-h-0 flex-1 overflow-hidden">
        {connectedDisplayPin ? (
          <DisplayMirrorPanel displayPin={connectedDisplayPin} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
            <Monitor className="h-14 w-14 text-gray-300 dark:text-gray-600" />
            <p className="text-base font-medium text-gray-600 dark:text-gray-300">Kein Display verbunden</p>
            <p className="max-w-lg text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              Auf der <strong className="text-gray-700 dark:text-gray-200">digitalen Tafel</strong> die Adresse{' '}
              {(() => {
                const url = typeof import.meta.env.VITE_APP_PUBLIC_URL === 'string' && import.meta.env.VITE_APP_PUBLIC_URL
                  ? import.meta.env.VITE_APP_PUBLIC_URL
                  : (typeof window !== 'undefined' ? window.location.origin : '');
                return url ? (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-800 break-all dark:bg-gray-800 dark:text-gray-200">
                    {url}
                  </span>
                ) : (
                  <span className="font-mono text-gray-700 dark:text-gray-300">[App-URL]</span>
                );
              })()}
              {' '}öffnen, auf <strong className="text-gray-700 dark:text-gray-200">„Display öffnen“</strong> klicken, danach hier die{' '}
              <strong className="text-gray-700 dark:text-gray-200">6-stellige PIN</strong> eingeben und koppeln.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
